/**
 * ThreadTabBar - Tab bar for thread navigation (Windsurf-style)
 *
 * Shows:
 * 1. Current thread tab (active, with title)
 * 2. "+" button to start a new chat
 * 3. Dropdown with search + full thread list ("All Conversations")
 */

import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Popover,
  TextField,
  InputAdornment,
  ButtonBase,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
  Divider,
  useMediaQuery,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import BookmarkBorderRounded from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRounded from '@mui/icons-material/BookmarkRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import SaveToSpaceModal from '../../../components/SaveToSpaceModal';
import ExportReportModal from './ExportReportModal';
import { listSpaces, createSpace } from '../../../services/spacesService';
import type { Space } from '../../../services/spacesService';
import { buildApiUrl, tenantApiPrefix } from '../../../config';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import { SnackbarContext } from '../../../ui/SnackbarProvider';

import { useNavigate } from 'react-router-dom';
import { useCopilotStore } from '../stores/useCopilotStore';
import type { CopilotThread } from '../stores/useCopilotStore';
import { formatRelativeTime } from '../../../utils/date';

interface ThreadTabBarProps {
  threadTitle: string | null;
  isStreaming: boolean;
}

export default function ThreadTabBar({ threadTitle, isStreaming }: ThreadTabBarProps) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { notify } = React.useContext(SnackbarContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);

  const threads = useCopilotStore((s: any) => s.threadList) as CopilotThread[];
  const currentThreadId = useCopilotStore((s: any) => s.threadId) as string | null;

  // Export report state
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Save to Space state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [spacesError, setSpacesError] = useState<string | null>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Check if current thread is already saved whenever threadId changes
  React.useEffect(() => {
    setIsSaved(false);
    if (!currentThreadId) return;
    const token = localStorage.getItem('klaris_jwt');
    const tid = getTenantIdFromStorage();
    if (!tid) return;
    fetch(
      buildApiUrl(`${tenantApiPrefix(tid)}/copilot/threads/${currentThreadId}/saved`),
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.saved) setIsSaved(true); })
      .catch(() => {});
  }, [currentThreadId]);

  const open = Boolean(anchorEl);

  const filteredThreads = useMemo(() => {
    if (!search.trim()) return threads;
    const q = search.toLowerCase();
    return threads.filter(
      (t) => (t.title || 'Untitled').toLowerCase().includes(q),
    );
  }, [threads, search]);

  const handleNewChat = () => {
    const newId = crypto.randomUUID();
    useCopilotStore.getState().resetSession();
    useCopilotStore.getState().setThreadId(newId);
    navigate(`/copilot/${newId}`);
    setAnchorEl(null);
  };

  const handleSelectThread = (t: CopilotThread) => {
    if (t.threadId !== currentThreadId) {
      useCopilotStore.getState().resetSession();
      useCopilotStore.getState().setThreadId(t.threadId);
      useCopilotStore.getState().setThreadTitle(t.title || null);
      navigate(`/copilot/${t.threadId}`);
    }
    setAnchorEl(null);
  };

  const handleOpenSaveModal = async () => {
    setSaveModalOpen(true);
    setSpacesLoading(true);
    setSpacesError(null);
    setSelectedSpaceId(null);
    setNewSpaceName('');
    setSubmitError(null);
    try {
      const loaded = await listSpaces();
      setSpaces(loaded);
    } catch (err: any) {
      setSpacesError(err.message || 'Failed to load spaces');
    } finally {
      setSpacesLoading(false);
    }
  };

  const handleSaveToSpace = async () => {
    if (!currentThreadId) return;
    if (!selectedSpaceId && !newSpaceName.trim()) {
      setSubmitError('Please select a category or create a new one');
      return;
    }
    setIsSaving(true);
    setSubmitError(null);
    try {
      let spaceId = selectedSpaceId;
      if (!spaceId && newSpaceName.trim()) {
        const newSpace = await createSpace(newSpaceName.trim());
        spaceId = newSpace.space_id;
      }
      if (!spaceId) throw new Error('No space selected');
      const token = localStorage.getItem('klaris_jwt');
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/copilot/threads/${currentThreadId}/save`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ space_id: spaceId }),
        },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      const result = await resp.json();
      setIsSaved(true);
      setSaveModalOpen(false);
      notify(result.already_exists ? 'Already saved in this space' : 'Saved to space!', result.already_exists ? 'info' : 'success');
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDropdown = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 150);
  };

  return (
    <>
      {/* Tab bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 38,
          px: 0.5,
          gap: 0,
          bgcolor: 'transparent',
          overflowX: 'auto',
          overflowY: 'hidden',
          flexShrink: 0,
          '&::-webkit-scrollbar': { height: 4 },
        }}
      >
        {/* Active tab */}
        <ButtonBase
          onClick={handleOpenDropdown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            borderRadius: '6px 6px 0 0',
            maxWidth: isNarrow ? 160 : 260,
            minWidth: 0,
            flexShrink: 1,
            bgcolor: isDark ? 'background.default' : '#fff',
            borderBottom: `2px solid ${theme.palette.primary.main}`,
            '&:hover': {
              bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.02),
            },
          }}
        >
          {isStreaming ? (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'success.main',
                flexShrink: 0,
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                },
              }}
            />
          ) : (
            <ChatBubbleOutlineRounded
              sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant="caption"
              noWrap
              title={threadTitle || 'Untitled chat'}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.primary',
                display: 'block',
              }}
            >
              {threadTitle || 'Untitled chat'}
            </Typography>
          </Box>
          <ExpandMoreRounded
            sx={{
              fontSize: 15,
              color: 'text.secondary',
              flexShrink: 0,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </ButtonBase>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Right cluster: primary New-chat button + overflow menu.
            We swapped three near-invisible icon buttons for one clearly-
            labeled primary action and a Material-style "More" menu. This
            makes the chat-level CTAs (PDF / Save / New) genuinely usable
            instead of forcing users to hover and decode tiny glyphs. */}
        <Tooltip title="Start a new chat (\u2318 N)" placement="bottom">
          <Button
            size="small"
            variant="text"
            onClick={handleNewChat}
            startIcon={<AddRounded sx={{ fontSize: 16 }} />}
            sx={{
              px: 1,
              py: 0.25,
              minHeight: 28,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: 1.25,
              '&:hover': {
                color: 'text.primary',
                bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.04),
              },
            }}
          >
            New chat
          </Button>
        </Tooltip>

        {threadTitle && (
          <>
            <Tooltip title="More actions" placement="bottom">
              <IconButton
                size="small"
                onClick={(e: React.MouseEvent<HTMLElement>) => setMoreAnchor(e.currentTarget)}
                sx={{ p: 0.5, ml: 0.25 }}
                aria-label="More actions"
              >
                <MoreVertRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={moreAnchor}
              open={Boolean(moreAnchor)}
              onClose={() => setMoreAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 0.5, minWidth: 200 } }}
            >
              <MenuItem
                onClick={() => {
                  setMoreAnchor(null);
                  handleOpenSaveModal();
                }}
              >
                <ListItemIcon>
                  {isSaved
                    ? <BookmarkRounded fontSize="small" sx={{ color: 'primary.main' }} />
                    : <BookmarkBorderRounded fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={isSaved ? 'Saved to space' : 'Save to space'}
                  primaryTypographyProps={{ fontSize: '0.8125rem' }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMoreAnchor(null);
                  setExportModalOpen(true);
                }}
              >
                <ListItemIcon>
                  <PictureAsPdfRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Export as PDF"
                  primaryTypographyProps={{ fontSize: '0.8125rem' }}
                />
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Dropdown popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 340,
              maxHeight: 420,
              borderRadius: 2,
              mt: 0.5,
              boxShadow: theme.shadows[8],
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Search */}
        <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder="Search conversations…"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.8rem', height: 34 },
            }}
          />
        </Box>

        <Divider />

        {/* Header */}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'text.disabled',
            }}
          >
            All Conversations
          </Typography>
        </Box>

        {/* Thread list */}
        <Box sx={{ overflow: 'auto', flex: 1, pb: 1 }}>
          {filteredThreads.length === 0 && (
            <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.disabled">
                {search ? 'No matching conversations' : 'No conversations yet'}
              </Typography>
            </Box>
          )}

          {filteredThreads.map((t: CopilotThread) => {
            const isActive = t.threadId === currentThreadId;
            return (
              <ButtonBase
                key={t.threadId}
                onClick={() => handleSelectThread(t)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, isDark ? 0.12 : 0.06)
                    : 'transparent',
                  '&:hover': {
                    bgcolor: isActive
                      ? alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08)
                      : isDark
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(0,0,0,0.03)',
                  },
                }}
              >
                <ChatBubbleOutlineRounded
                  sx={{
                    fontSize: 14,
                    color: isActive ? 'primary.main' : 'text.disabled',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  noWrap
                  title={t.title || 'Untitled'}
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'text.primary' : 'text.secondary',
                    flex: '1 1 0%',
                    minWidth: 0,
                    textAlign: 'left',
                  }}
                >
                  {t.title || 'Untitled'}
                </Typography>
                {t.createdAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6875rem',
                      color: 'text.disabled',
                      flexShrink: 0,
                      ml: 0.5,
                    }}
                  >
                    {formatRelativeTime(t.createdAt!)}
                  </Typography>
                )}
              </ButtonBase>
            );
          })}
        </Box>
      </Popover>

      {/* Save to Space Modal */}
      <SaveToSpaceModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        spaces={spaces}
        loading={spacesLoading}
        error={spacesError}
        selectedSpaceId={selectedSpaceId}
        setSelectedSpaceId={setSelectedSpaceId}
        newSpaceName={newSpaceName}
        setNewSpaceName={setNewSpaceName}
        onSubmit={handleSaveToSpace}
        creating={isSaving}
        submitError={submitError}
      />

      {/* Export Report Modal */}
      {currentThreadId && (
        <ExportReportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          threadId={currentThreadId}
        />
      )}
    </>
  );
}
